import React, { useMemo, useState } from 'react';
import { UserIcon, PackageIcon, MapPinIcon, CheckIcon, AlertTriangleIcon, CheckCircleIcon, PlusIcon, MinusIcon, SearchIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { findUsageType } from './bookingHelpers';
import { RESOURCES, BOOKING_FILES } from '../../data/windsor';
import type { Activity, ActivityResource, ActivitySlot, Resource } from '../../types/loisirs';
interface Props {
  /** Slots the activity will run on — drives the availability check. */
  slots: ActivitySlot[];
  /** Venue picked in the Identification block, for the read-only Lieu row. */
  venue: {
    id: string;
    name: {
      fr: string;
      en: string;
    };
    occupancy: number;
  };
  /** Conflicts already detected on the venue by the parent form. */
  venueConflicts: string[];
  /** Other activities, used to spot a resource booked twice at once. */
  existing: Activity[];
  /** Activity being edited, excluded from its own conflicts. */
  activityId?: string;
  selected: ActivityResource[];
  onChange: (next: ActivityResource[]) => void;
}

/** "18:30" -> 18.5 */
function toDecimal(time: string) {
  const [h, m] = time.split(':').map(Number);
  return h + (m || 0) / 60;
}
const WEEKDAYS: Record<number, {
  fr: string;
  en: string;
}> = {
  1: {
    fr: 'lundi',
    en: 'Monday'
  },
  2: {
    fr: 'mardi',
    en: 'Tuesday'
  },
  3: {
    fr: 'mercredi',
    en: 'Wednesday'
  },
  4: {
    fr: 'jeudi',
    en: 'Thursday'
  },
  5: {
    fr: 'vendredi',
    en: 'Friday'
  },
  6: {
    fr: 'samedi',
    en: 'Saturday'
  },
  7: {
    fr: 'dimanche',
    en: 'Sunday'
  }
};
export function ActivityResources({
  slots,
  venue,
  venueConflicts,
  existing,
  activityId,
  selected,
  onChange
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [search, setSearch] = useState('');
  const staff = RESOURCES.filter(r => r.kind === 'humain' && r.bookable);
  const gear = RESOURCES.filter(r => r.kind === 'equipement' && r.bookable);
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });

  /**
   * Busy windows for every resource, gathered once: other activities that
   * require it, plus facility bookings that reserve it directly.
   */
  const busy = useMemo(() => {
    const map: Record<string, {
      weekday: number;
      from: number;
      to: number;
      who: string;
    }[]> = {};
    const push = (resourceId: string, entry: {
      weekday: number;
      from: number;
      to: number;
      who: string;
    }) => {
      map[resourceId] = [...(map[resourceId] ?? []), entry];
    };
    existing.filter(a => a.id !== activityId).forEach(a => (a.resources ?? []).forEach(need => a.slots.forEach(slot => push(need.resourceId, {
      weekday: slot.weekday,
      from: toDecimal(slot.startTime),
      to: toDecimal(slot.endTime),
      who: a.name[lang]
    }))));
    BOOKING_FILES.filter(b => b.status !== 'annulee').forEach(b => b.slots.forEach(slot => {
      const day = new Date(`${slot.date}T12:00:00`).getDay();
      push(b.resourceId, {
        weekday: day === 0 ? 7 : day,
        from: toDecimal(slot.startTime),
        to: toDecimal(slot.endTime),
        who: `${b.client.name} — ${findUsageType(b.usageTypeId)?.label[lang] ?? ''}`
      });
    }));
    return map;
  }, [existing, activityId, lang]);

  /** Clashes between the activity's slots and a resource's busy windows. */
  const clashesFor = (resourceId: string) => {
    const windows = busy[resourceId] ?? [];
    const found: string[] = [];
    slots.forEach(slot => {
      const from = toDecimal(slot.startTime);
      const to = toDecimal(slot.endTime);
      if (to <= from) return;
      windows.forEach(window => {
        if (window.weekday !== slot.weekday) return;
        if (window.from < to && window.to > from) {
          found.push(t(`${window.who} — ${WEEKDAYS[slot.weekday].fr} ${slot.startTime}`, `${window.who} — ${WEEKDAYS[slot.weekday].en} ${slot.startTime}`));
        }
      });
    });
    return Array.from(new Set(found));
  };
  const isPicked = (resourceId: string) => selected.some(r => r.resourceId === resourceId);
  const quantityOf = (resourceId: string) => selected.find(r => r.resourceId === resourceId)?.quantity ?? 1;
  const toggle = (resource: Resource) => {
    onChange(isPicked(resource.id) ? selected.filter(r => r.resourceId !== resource.id) : [...selected, {
      resourceId: resource.id,
      quantity: 1
    }]);
  };
  const setQuantity = (resourceId: string, quantity: number) => {
    onChange(selected.map(r => r.resourceId === resourceId ? {
      ...r,
      quantity: Math.max(1, quantity)
    } : r));
  };

  /** Total clashes across everything the activity needs, venue included. */
  const pickedClashes = selected.flatMap(need => {
    const resource = RESOURCES.find(r => r.id === need.resourceId);
    if (!resource) return [];
    return clashesFor(need.resourceId).map(clash => `${resource.name[lang]} · ${clash}`);
  });
  const allClear = selected.length > 0 && pickedClashes.length === 0 && venueConflicts.length === 0;

  /** Estimated staff cost per session, a useful sanity check when planning. */
  const hoursPerSession = slots.reduce((max, slot) => Math.max(max, toDecimal(slot.endTime) - toDecimal(slot.startTime)), 0);
  const staffCost = selected.reduce((sum, need) => {
    const resource = RESOURCES.find(r => r.id === need.resourceId);
    if (!resource || resource.kind !== 'humain') return sum;
    return sum + (resource.rateOrganization ?? 0) * need.quantity * hoursPerSession;
  }, 0);
  const Group = ({
    items,
    title,
    hint,
    icon: Icon,
    quantities
  }: {
    items: Resource[];
    title: string;
    hint: string;
    icon: typeof UserIcon;
    quantities: boolean;
  }) => {
    const q = search.trim().toLowerCase();
    const shown = items.filter(item => q ? item.name[lang].toLowerCase().includes(q) : true);
    if (shown.length === 0) return null;
    return <div data-id="element-2367" data-mp="306wbj9ntpxy6">
        <h4 className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2368" data-mp="3075vafi9z8vf">
          <Icon className="w-3.5 h-3.5" data-id="element-2369" data-mp="3dxj0ia3s89h2" />
          {title}
          <span className="font-normal normal-case tracking-normal text-slate-400" data-id="element-2370" data-mp="39yikt0glpmya">
            · {hint}
          </span>
        </h4>
        <ul className="space-y-2" data-id="element-2371" data-mp="3nx06ytquyre3">
          {shown.map(item => {
          const picked = isPicked(item.id);
          const clashes = clashesFor(item.id);
          const busyWhilePicked = picked && clashes.length > 0;
          return <li key={item.id} className={`rounded-lg border px-4 py-3 transition-colors ${busyWhilePicked ? 'border-amber-300 bg-amber-50/60' : picked ? 'border-[#4F46E5] bg-indigo-50/50 ring-1 ring-[#4F46E5]/20' : 'border-slate-200'}`} data-id="element-2372" data-mp="1okxo9lq2611h">
                <div className="flex items-center gap-3 flex-wrap" data-id="element-2373" data-mp="3vg9dmf7zcbzg">
                  <button onClick={() => toggle(item)} aria-pressed={picked} className="flex items-center gap-3 min-w-0 flex-1 text-left" data-id="element-2374" data-mp="3hh9oz7kgvx3a">
                    <span role="checkbox" aria-checked={picked} className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${picked ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : 'bg-white border-slate-300'}`} data-id="element-2375" data-mp="37jltgff1elk2">
                      {picked && <CheckIcon className="w-3.5 h-3.5" data-id="element-2376" data-mp="3um1j2z2424gc" />}
                    </span>
                    <span className="min-w-0" data-id="element-2377" data-mp="3pp2u0uxb0np0">
                      <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-2378" data-mp="3kx1g74khmowv">
                        {item.name[lang]}
                      </span>
                      <span className="block text-xs text-slate-500" data-id="element-2379" data-mp="3fxokrvfjw9io">
                        {item.kind === 'humain' ? `${money(item.rateOrganization ?? 0)} / h` : (item.rateCitizen ?? 0) === 0 ? t('inventaire municipal', 'municipal inventory') : `${money(item.rateCitizen ?? 0)} ${t('par location', 'per rental')}`}
                      </span>
                    </span>
                  </button>

                  {/* Availability badge */}
                  {clashes.length === 0 ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200 whitespace-nowrap" data-id="element-2380" data-mp="v8iq75ryzo76">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-2381" data-mp="17oqu3io1xvuy" />
                      {t('Disponible', 'Available')}
                    </span> : <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200 whitespace-nowrap" data-id="element-2382" data-mp="1hgefp2xxgioi">
                      <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-2383" data-mp="oqxog8m1n7tv" />
                      {t('Occupé', 'Busy')}
                      <span className="tabular-nums" data-id="element-2384" data-mp="tqaltnajjxap">{clashes.length}</span>
                    </span>}

                  {/* Quantity, for equipment and pooled roles */}
                  {quantities && picked && <div className="inline-flex items-center rounded-lg border border-slate-200 overflow-hidden shrink-0" data-id="element-2385" data-mp="255fr8z2249y0">
                      <button onClick={() => setQuantity(item.id, quantityOf(item.id) - 1)} aria-label={t('Retirer une unité', 'Remove one unit')} className="px-2 py-1.5 text-slate-500 hover:bg-slate-50" data-id="element-2386" data-mp="2a6n0l2julh6s">
                        <MinusIcon className="w-3.5 h-3.5" data-id="element-2387" data-mp="6s9tcvwso5f2" />
                      </button>
                      <span className="px-2.5 text-sm font-semibold text-slate-800 tabular-nums" data-id="element-2388" data-mp="369ht9l57sbxz">
                        {quantityOf(item.id)}
                      </span>
                      <button onClick={() => setQuantity(item.id, quantityOf(item.id) + 1)} aria-label={t('Ajouter une unité', 'Add one unit')} className="px-2 py-1.5 text-slate-500 hover:bg-slate-50" data-id="element-2389" data-mp="3f9e8yx9mm0gw">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-2390" data-mp="zgfdbgg0xqdu" />
                      </button>
                    </div>}
                </div>

                {busyWhilePicked && <ul className="mt-2 pl-8 space-y-0.5" data-id="element-2391" data-mp="2f1g9s15ilive">
                    {clashes.map(clash => <li key={clash} className="text-xs text-amber-800" data-id="element-2392" data-mp="2a8n0r130jodi">
                        • {clash}
                      </li>)}
                  </ul>}
              </li>;
        })}
        </ul>
      </div>;
  };
  return <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2393" data-mp="1w9nf82asrplk">
      <div className="flex items-start justify-between gap-4 mb-1 flex-wrap" data-id="element-2394" data-mp="1rau0y7norukc">
        <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-2395" data-mp="1mcafs9gx2mt1">
          {t('Ressources nécessaires', 'Required resources')}
        </h3>
        <div className="relative" data-id="element-2396" data-mp="3lobs04nav9k4">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-2397" data-mp="3lnae7sewg8yp" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Filtrer les ressources…', 'Filter resources…')} aria-label={t('Filtrer les ressources', 'Filter resources')} className="w-52 pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-2398" data-mp="1f14foyr0cib1" />
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-5 max-w-2xl" data-id="element-2399" data-mp="1xpj2tay7fc6k">
        {t('Le personnel et le matériel dont l’activité a besoin. La disponibilité est vérifiée sur les créneaux choisis, contre les autres activités et les réservations de plateaux.', 'The staff and equipment the activity needs. Availability is checked on the selected slots, against other activities and facility bookings.')}
      </p>

      {/* Venue — chosen in the Identification block */}
      <div className="mb-5" data-id="element-2400" data-mp="heow9rf0taku">
        <h4 className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2401" data-mp="9tur658grmwc">
          <MapPinIcon className="w-3.5 h-3.5" data-id="element-2402" data-mp="4v0955ibm2da" />
          {t('Lieu', 'Venue')}
          <span className="font-normal normal-case tracking-normal text-slate-400" data-id="element-2403" data-mp="4x4apgsqypae">
            · {t('choisi à l’identification', 'set in Identification')}
          </span>
        </h4>
        <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 flex-wrap ${venueConflicts.length > 0 ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-slate-50/60'}`} data-id="element-2404" data-mp="equyejh1uvp1">
          <span className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-2405" data-mp="u7z4a1mjtfy">
            <MapPinIcon className="w-3.5 h-3.5" data-id="element-2406" data-mp="3s5razt9dv31x" />
          </span>
          <span className="min-w-0 flex-1" data-id="element-2407" data-mp="34gykwdffzpf8">
            <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-2408" data-mp="34fx0hicjdqku">
              {venue.name[lang]}
            </span>
            <span className="block text-xs text-slate-500" data-id="element-2409" data-mp="22skw7yg5pnop">
              {t('Occupation actuelle : ', 'Current occupancy: ')}
              {venue.occupancy} %
            </span>
          </span>
          {venueConflicts.length > 0 ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200" data-id="element-2410" data-mp="2br57wfnaux3f">
              <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-2411" data-mp="2lj2jvom4tt37" />
              {t('Occupé', 'Busy')}
              <span className="tabular-nums" data-id="element-2412" data-mp="2qgkx3r76muh0">{venueConflicts.length}</span>
            </span> : <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200" data-id="element-2413" data-mp="2uk0rshzoykr2">
              <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-2414" data-mp="104y8x355e0x4" />
              {t('Disponible', 'Available')}
            </span>}
        </div>
      </div>

      <div className="space-y-5" data-id="element-2415" data-mp="154jfnzqk7dke">
        <Group items={staff} title={t('Personnel', 'Staff')} hint={t('professeur, sauveteur, surveillant', 'teacher, lifeguard, attendant')} icon={UserIcon} quantities data-id="element-2416" data-mp="xkiaovoghh52" />
        <Group items={gear} title={t('Matériel', 'Equipment')} hint={t('prélevé sur l’inventaire municipal', 'drawn from municipal inventory')} icon={PackageIcon} quantities data-id="element-2417" data-mp="ntxfli7zquqw" />
      </div>

      {/* Availability summary */}
      <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-2418" data-mp="1bkjccdcjosll">
        {selected.length === 0 ? <p className="text-sm text-slate-500" data-id="element-2419" data-mp="16kwyq2st1ac9">
            {t('Aucune ressource sélectionnée. Le lieu seul sera réservé pour cette activité.', 'No resource selected. Only the venue will be reserved for this activity.')}
          </p> : allClear ? <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-2420" data-mp="3na2ev7tadkgp">
            <p className="flex items-start gap-2 text-sm text-emerald-800" data-id="element-2421" data-mp="3nc6rqf95xtt4">
              <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" data-id="element-2422" data-mp="3orrb21ava1cc" />
              {t(`Les ${selected.length} ressources sont libres sur tous les créneaux choisis.`, `All ${selected.length} resources are free on every selected slot.`)}
            </p>
            {staffCost > 0 && <p className="text-sm text-slate-500" data-id="element-2423" data-mp="3608ae14bezdh">
                {t('Coût de personnel estimé : ', 'Estimated staff cost: ')}
                <span className="font-semibold text-slate-800 tabular-nums" data-id="element-2424" data-mp="3asrocz6wzwit">
                  {money(Math.round(staffCost))}
                </span>
                {t(' par séance', ' per session')}
              </p>}
          </div> : <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3" data-id="element-2425" data-mp="2n1443045rjpi">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-1.5" data-id="element-2426" data-mp="2r2zgaznmnhi2">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-2427" data-mp="28enjevplalwy" />
              {t('Ressources déjà engagées sur ces plages', 'Resources already committed on these slots')}
            </p>
            <ul className="space-y-1" data-id="element-2428" data-mp="2hfvfd74mvanv">
              {pickedClashes.map(clash => <li key={clash} className="text-xs text-amber-800" data-id="element-2429" data-mp="2cg0pn6wlgr4e">
                  • {clash}
                </li>)}
              {venueConflicts.map(clash => <li key={clash} className="text-xs text-amber-800" data-id="element-2430" data-mp="2meq1ahxwdfc7">
                  • {venue.name[lang]} · {clash}
                </li>)}
            </ul>
            <p className="text-xs text-amber-700/80 mt-2" data-id="element-2431" data-mp="3087hbsrdk9xh">
              {t('Vous pouvez enregistrer malgré tout — un remplacement ou un déplacement de créneau sera à confirmer.', 'You can still save — a replacement or a slot change will need confirmation.')}
            </p>
          </div>}
      </div>
    </section>;
}